import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Check, Mail } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import PhoneMockup from "@/components/PhoneMockup";

const EASE = [0.16, 1, 0.3, 1];

function Mask({ children, i = 0 }) {
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.95, ease: EASE, delay: 0.15 + i * 0.14 }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const rotate = useTransform(scrollYProgress, [0, 1], [0, -5]);

    return (
        <section ref={ref} className="relative overflow-hidden pb-16 pt-28 sm:pb-24 sm:pt-32 lg:pt-40">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-48 right-[-12%] h-[620px] w-[620px] rounded-full bg-fynary-lime/10 blur-[150px]"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
            />

            <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8 lg:px-12">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
                        className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-fynary-surface px-4 py-1.5 text-xs font-medium text-fynary-muted sm:text-sm"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-fynary-lime" />
                        fynary. for iOS & Android
                    </motion.div>

                    <h1
                        data-testid="hero-title"
                        className="font-display text-[2.9rem] font-extrabold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl"
                    >
                        <Mask i={0}>Your money,</Mask>
                        <Mask i={1}>
                            <span className="text-fynary-lime">explained.</span>
                        </Mask>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.55 }}
                        className="mt-6 max-w-lg text-base leading-relaxed text-fynary-muted sm:text-lg"
                    >
                        fynary. answers the only money question that matters — <em className="not-italic text-white">where did my money go?</em>{" "}
                        Import the CSV statement you export from your bank and get a clear, private picture of your month: spending,
                        income, savings and trends.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.7 }}
                        className="mt-8 flex flex-wrap items-center gap-3"
                    >
                        <button
                            data-testid="hero-cta-primary"
                            onClick={() => scrollToId("how-it-works")}
                            className="inline-flex items-center gap-2 rounded-full bg-fynary-lime px-7 py-3.5 font-display text-base font-semibold text-fynary-bg transition-[filter,transform] duration-200 hover:brightness-110 active:scale-[0.98]"
                        >
                            See how it works
                            <ArrowDown className="h-4 w-4" />
                        </button>
                        <a
                            data-testid="hero-cta-secondary"
                            href="mailto:workfynary@gmail.com"
                            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-fynary-surface px-7 py-3.5 font-display text-base font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-fynary-surface2 active:scale-[0.98]"
                        >
                            <Mail className="h-4 w-4" />
                            Write to us
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-medium text-fynary-muted sm:text-sm"
                    >
                        {["No bank syncing", "No passwords", "No PINs", "No OTPs"].map((t) => (
                            <span key={t} className="flex items-center gap-1.5">
                                <Check className="h-3.5 w-3.5 text-fynary-lime" />
                                {t}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: EASE, delay: 0.4 }}
                    style={{ y, rotate }}
                    className="relative lg:pl-6"
                >
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-fynary-lime/[0.07] blur-[110px]"
                    />
                    <PhoneMockup screen="dashboard" testid="hero-phone-mockup" />
                </motion.div>
            </div>
        </section>
    );
}
